import { APP_BUILD_MODE, IS_DEV, VITE_USER_BRANCH } from "@/utils/env";
import { NOTIFICATION_INBOX_BROADCAST_CHANNEL } from "./notificationConstants";
import { parseInboxEnvelope, serializeInboxEnvelope } from "./inboxReducer";

/**
 * @typedef {import('./inboxReducer').InboxItem} InboxItem
 */

const APP_NOTIFICATION_PREFIX = "cai3p0.notifications";

/**
 * Build localStorage key: app + mode + branch + customer + user (spec §6).
 *
 * @param {string} userId
 * @param {string} customerId
 * @returns {string}
 */
export function buildNotificationStorageKey(userId, customerId) {
  const mode = APP_BUILD_MODE;
  const branch = VITE_USER_BRANCH;
  const u = String(userId || "").trim() || "_guest";
  const c = String(customerId || "").trim() || "_none";
  return `${APP_NOTIFICATION_PREFIX}:${mode}:${branch}:${c}:${u}`;
}

/**
 * Remove the inbox bucket for this scope (logout).
 *
 * @param {string} userId
 * @param {string} customerId
 */
export function clearNotificationStorageForScope(userId, customerId) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(buildNotificationStorageKey(userId, customerId));
  } catch {
    // Quota or private mode — best effort
  }
}

/**
 * @param {string} storageKey
 * @returns {InboxItem[]}
 */
export function loadInboxItems(storageKey) {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null || raw === "") return [];
    const parsed = JSON.parse(raw);
    const items = parseInboxEnvelope(parsed);
    return items ?? [];
  } catch {
    return [];
  }
}

/**
 * @param {string} storageKey
 * @param {InboxItem[]} items
 */
export function saveInboxItemsSync(storageKey, items) {
  if (typeof localStorage === "undefined") return;
  try {
    const envelope = serializeInboxEnvelope(items);
    localStorage.setItem(storageKey, JSON.stringify(envelope));
    broadcastInboxSync(storageKey);
  } catch (e) {
    if (IS_DEV) {
      console.warn("[notificationPersistence] save failed:", e);
    }
  }
}

/**
 * @param {string} storageKey
 */
function broadcastInboxSync(storageKey) {
  try {
    const ch = getBroadcastChannel();
    ch?.postMessage({ type: "inbox-updated", storageKey });
  } catch {
    // ignore
  }
}

/** @type {BroadcastChannel | null} */
let _bc = null;

function getBroadcastChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!_bc) {
    try {
      _bc = new BroadcastChannel(NOTIFICATION_INBOX_BROADCAST_CHANNEL);
    } catch {
      return null;
    }
  }
  return _bc;
}

/**
 * Subscribe to cross-tab inbox updates. Invokes callback when another tab writes the same key.
 *
 * @param {(key: string) => void} onExternalUpdate
 * @returns {() => void} unsubscribe
 */
export function subscribeInboxCrossTab(onExternalUpdate) {
  const handlerStorage = (e) => {
    if (e.key && e.key.startsWith(`${APP_NOTIFICATION_PREFIX}:`)) {
      onExternalUpdate(e.key);
    }
  };

  let bc = null;
  const handlerBc = (event) => {
    const data = event?.data;
    if (data?.type === "inbox-updated" && typeof data.storageKey === "string") {
      onExternalUpdate(data.storageKey);
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("storage", handlerStorage);
  }

  try {
    bc = getBroadcastChannel();
    bc?.addEventListener("message", handlerBc);
  } catch {
    // ignore
  }

  return () => {
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", handlerStorage);
    }
    try {
      bc?.removeEventListener("message", handlerBc);
    } catch {
      // ignore
    }
  };
}
